import { Form } from 'informed';
import { FormattedMessage } from 'react-intl';

import { useStyle } from '@/classify';
import { useScrollLock } from '@/common/hooks/useScrollLock';
import Image from 'next/image';

import Button from '../Button';
import { Portal } from '../Portal';
import defaultClasses from './dialog.module.css';

const Dialog = props => {
  const {
    cancelText = 'Cancel',
    cancelTranslationId = 'global.cancelButton',
    children,
    confirmText = 'Confirm',
    confirmTranslationId = 'global.confirmButton',
    isForm = true,
    formProps,
    isModal = false,
    isOpen,
    onCancel,
    onConfirm,
    width = 640,
    shouldDisableHeader = false,
    shouldDisableAllButtons,
    shouldDisableConfirmButton,
    shouldShowButtons = true,
    shouldShowHeader = true,
    shouldUnmountOnHide = true,
    title,
    customStyle = '', // 自定义样式registertester使用
  } = props;

  // Prevent the page from scrolling in the background
  // when the Dialog is open.
  useScrollLock(isOpen);

  const classes = useStyle(defaultClasses, props.classes);
  const rootClass = isOpen ? classes.root_open : classes.root;
  const isMaskDisabled = shouldDisableAllButtons || isModal;
  const confirmButtonDisabled = shouldDisableAllButtons || shouldDisableConfirmButton;

  const cancelButtonClasses = {
    root_lowPriority: classes.cancelButton,
  };
  const confirmButtonClasses = {
    root_highPriority: classes.confirmButton,
  };

  const maybeCloseXButton = !isModal ? (
    <button
      className={classes.headerButton}
      disabled={shouldDisableAllButtons}
      onClick={onCancel}
      type='reset'
    >
      <Image
        src={`/icons/modal_close.svg`}
        width={16}
        height={16}
      />
    </button>
  ) : null;

  const maybeHeader = !shouldDisableHeader ? (
    <div className={classes.header}>
      <span className={classes.headerText}>{title}</span>
      {maybeCloseXButton}
    </div>
  ) : null;

  const maybeButtons = shouldShowButtons ? (
    <div className={classes.buttons}>
      <Button
        classes={cancelButtonClasses}
        disabled={shouldDisableAllButtons}
        onClick={onCancel}
        priority='low'
        type='reset'
      >
        <FormattedMessage
          id={cancelTranslationId}
          defaultMessage={cancelText}
        />
      </Button>
      <Button
        classes={confirmButtonClasses}
        disabled={confirmButtonDisabled}
        priority='high'
        type='submit'
      >
        <FormattedMessage
          id={confirmTranslationId}
          defaultMessage={confirmText}
        />
      </Button>
    </div>
  ) : null;

  const parsedWidth = typeof width === 'number' ? `${width}px` : width;

  const DialogStyle =
    customStyle == 'registerTesterPage' ? [classes.dialog, classes.registerTesterPage].join(' ') : classes.dialog;

  const maybeForm = isForm ? (
    <Form
      className={classes.form}
      {...formProps}
      onSubmit={onConfirm}
    >
      {/* The Mask. */}
      <button
        className={classes.mask}
        disabled={isMaskDisabled}
        onClick={onCancel}
        type='reset'
      />
      {/* The Dialog. */}
      <div
        className={classes.dialog}
        style={{
          width: parsedWidth,
        }}
        data-cy={title}
      >
        {maybeHeader}
        <div className={classes.body}>
          <div className={classes.contents}>{children}</div>
          {maybeButtons}
        </div>
      </div>
    </Form>
  ) : (
    <div className={classes.container}>
      {/* The Mask. */}
      <button
        className={classes.mask}
        disabled={isMaskDisabled}
        onClick={onCancel}
        type='reset'
      />
      {/* The Dialog. */}
      <div
        className={DialogStyle}
        style={{
          width: parsedWidth,
        }}
        data-cy={title}
      >
        {maybeHeader}
        <div className={classes.body}>
          <div className={classes.contents}>{children}</div>
          {maybeButtons}
        </div>
      </div>
    </div>
  );

  const content = isOpen || !shouldUnmountOnHide ? maybeForm : null;

  return (
    <Portal>
      <aside className={rootClass}>{content}</aside>
    </Portal>
  );
};

export default Dialog;
